/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ArrowPathIcon, CloudArrowUpIcon, LockClosedIcon } from '@heroicons/react/20/solid';
import Button from '../../elements/Button';

export default function GettingStarted({ displayedData, showSync, showImport, category, connectedAccounts, handleNavigateToAccount }) {
  const { t } = useTranslation('accountDetails', { keyPrefix: 'gettingStarted' });

  const { heading, subheading, intro, features } = (() => {
    if (displayedData === 'cashflow') {
      return {
        heading: t('cashflow.heading'),
        subheading: t('cashflow.subheading'),
        intro: t('cashflow.intro'),
        features: [],
      };
    }

    if (displayedData === 'values') {
      return {
        heading: t('values.heading'),
        subheading: t('values.subheading'),
        intro: t('values.intro'),
        features: [
          {
            name: t('values.features.0.name'),
            description: t('values.features.0.description'),
            icon: LockClosedIcon,
          },
          {
            name: t('values.features.1.name'),
            description: t('values.features.1.description'),
            icon: CloudArrowUpIcon,
          },
        ],
      };
    }

    const holdingsFeatures = [
      {
        name: t('features.0.name'),
        description: t('features.0.description'),
        icon: CloudArrowUpIcon,
      },
    ];

    if (showImport) {
      holdingsFeatures.push({
        name: t('features.1.name'),
        description: t('features.1.description'),
        icon: LockClosedIcon,
      });
    }

    if (showSync) {
      holdingsFeatures.push({
        name: t('features.2.name'),
        description: t('features.2.description'),
        icon: ArrowPathIcon,
      });
    }

    return {
      heading: t('heading'),
      subheading: t('subheading'),
      intro: t('intro'),
      features: holdingsFeatures,
    };
  })();

  return (
    <div className="bg-white py-8 w-full">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-3xl lg:text-center">
          <h2 className="text-base font-semibold leading-8 tracking-wide text-brandBlue-500 uppercase">{heading}</h2>
          <p className={`mt-2 ${displayedData === 'cashflow' ? 'text-2xl sm:text-3xl font-semibold' : 'text-3xl sm:text-4xl font-bold'} text-gray-900`}>{subheading}</p>
          <p className="mt-4 text-base leading-7 text-gray-600">{intro}</p>
          {displayedData === 'cashflow' && (connectedAccounts?.length || 0) > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-base leading-6 text-gray-700">{t('cashflow.navigateIntro')}</p>
              <div className="flex flex-wrap gap-2 justify-center pt-6">
                {connectedAccounts.map((acc) => (
                  <Button key={acc.id} text={acc.name} withAccent size="xl" onClick={() => handleNavigateToAccount && handleNavigateToAccount(acc.id)} formatting="!min-w-0" />
                ))}
              </div>
            </div>
          )}
        </div>
        {features.length > 0 && (
          <div className="mx-auto mt-8 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {features.map((feature, idx) => (
                <>
                  {/* if there are 2 features, leave the middle one free */}
                  {features.length === 2 && idx === 1 && <div />}

                  <div key={feature.name} className="flex flex-col">
                    <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                      <feature.icon className="h-5 w-5 flex-none text-brandBlue-500" aria-hidden="true" />
                      {feature.name}
                    </dt>
                    <dd className="mt-4 flex flex-auto flex-col text-base leading-6 text-gray-600">
                      <p className="flex-auto">{feature.description}</p>
                    </dd>
                  </div>
                </>
              ))}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
GettingStarted.propTypes = {
  showSync: PropTypes.bool,
  showImport: PropTypes.bool,
  displayedData: PropTypes.oneOf(['holdings', 'cashflow', 'quotes', 'values']),
  category: PropTypes.string,
  connectedAccounts: PropTypes.arrayOf(PropTypes.object),
  handleNavigateToAccount: PropTypes.func,
};
GettingStarted.defaultProps = {
  showSync: false,
  showImport: false,
  displayedData: 'holdings',
  category: null,
  connectedAccounts: [],
  handleNavigateToAccount: null,
};
